/**
 * Client for the My Health Record FHIR® Gateway.
 *
 * Walks the retrieval sequence a provider-side app performs against the real
 * gateway (API Specification v5.0, DH-4234:2026) and reports each interaction
 * as it lands, so the console can show the record arriving rather than
 * appearing all at once.
 *
 * Points at the mock gateway by default; swap MHR_BASE for the production FQDN
 * and the call sequence is unchanged.
 */

const IHI_SYSTEM = 'http://ns.electronichealth.net.au/id/hi/ihi/1.0';
const LOINC_OID = '2.16.840.1.113883.6.1';

export const DOC_CLASS = {
  sharedHealthSummary: `60591-5^^${LOINC_OID}`,
  eventSummary: `34133-9^^${LOINC_OID}`,
  dischargeSummary: `18842-5^^${LOINC_OID}`,
  pathologyReport: '100.32001^^1.2.36.1.2001.1001.101',
  diagnosticImaging: '100.16957^^1.2.36.1.2001.1001.101'
};

/** Default demo record — matches health_record_scenario_1.json. */
export const DEMO_IHI = '8003601234567890';

const CDA_SECTION_CODES = {
  allergies: '48765-2',
  medications: '10160-0',
  medicalHistory: '11348-0',
  immunisations: '11369-6'
};

class MhrError extends Error {
  constructor(message, { status, outcome } = {}) {
    super(message);
    this.name = 'MhrError';
    this.status = status;
    this.outcome = outcome;
  }
}

function outcomeText(body) {
  const issue = body?.issue?.[0];
  return issue?.details?.text || issue?.diagnostics || null;
}

function entries(bundle, resourceType) {
  const all = (bundle?.entry || []).map(e => e.resource).filter(Boolean);
  return resourceType ? all.filter(r => r.resourceType === resourceType) : all;
}

function decodeBase64(value) {
  const binary = atob(String(value || ''));
  const bytes = Uint8Array.from(binary, c => c.charCodeAt(0));
  return new TextDecoder('utf-8').decode(bytes);
}

// --- CDA parsing ------------------------------------------------------------

function sectionByCode(doc, code) {
  return Array.from(doc.getElementsByTagName('section'))
    .find(s => s.getElementsByTagName('code')[0]?.getAttribute('code') === code) || null;
}

function cdaDateToIso(value) {
  const raw = String(value || '');
  if (raw.length < 8) return raw || null;
  return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
}

/**
 * Pull the past medical history and immunisation sections out of a Shared
 * Health Summary. MHR exposes allergies and medications as discrete FHIR, but
 * conditions and immunisations only exist inside the CDA payload.
 */
export function parseSharedHealthSummary(xml) {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) {
    throw new MhrError('The Shared Health Summary payload could not be parsed.');
  }

  const historySection = sectionByCode(doc, CDA_SECTION_CODES.medicalHistory);
  const medicalHistory = historySection
    ? Array.from(historySection.getElementsByTagName('observation')).map(obs => {
        const value = obs.getElementsByTagName('value')[0];
        return {
          date: cdaDateToIso(obs.getElementsByTagName('effectiveTime')[0]?.getAttribute('value')),
          condition: value?.getAttribute('displayName') || 'Unknown',
          code: value?.getAttribute('code') || null,
          system: 'SNOMED CT-AU'
        };
      })
    : [];

  const immSection = sectionByCode(doc, CDA_SECTION_CODES.immunisations);
  const immunisations = immSection
    ? Array.from(immSection.getElementsByTagName('substanceAdministration')).map(sa => {
        const material = sa.getElementsByTagName('manufacturedMaterial')[0];
        const code = material?.getElementsByTagName('code')[0];
        return {
          vaccine: code?.getAttribute('displayName') || 'Unknown',
          code: code?.getAttribute('code') || null,
          system: 'SNOMED CT-AU',
          date: cdaDateToIso(sa.getElementsByTagName('effectiveTime')[0]?.getAttribute('value'))
        };
      })
    : [];

  const title = doc.getElementsByTagName('title')[0]?.textContent || 'Shared Health Summary';
  const custodian = doc.getElementsByTagName('representedCustodianOrganization')[0]
    ?.getElementsByTagName('name')[0]?.textContent || null;

  return { medicalHistory, immunisations, title, custodian };
}

/** Parse the XML returned by Binary/View?viewtype=PathologyView. */
export function parsePathologyView(xml) {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length) return [];
  return Array.from(doc.getElementsByTagName('result')).map(r => {
    const value = r.getElementsByTagName('value')[0];
    return {
      name: r.getElementsByTagName('name')[0]?.textContent || 'Unknown',
      code: r.getElementsByTagName('name')[0]?.getAttribute('code') || null,
      collected: r.getElementsByTagName('collectionDateTime')[0]?.getAttribute('value') || null,
      value: value?.textContent || null,
      unit: value?.getAttribute('unit') || '',
      referenceRange: r.getElementsByTagName('referenceRange')[0]?.textContent || '',
      interpretation: r.getElementsByTagName('interpretation')[0]?.textContent || '',
      interpretationCode: r.getElementsByTagName('interpretation')[0]?.getAttribute('code') || 'N',
      pathologist: r.getElementsByTagName('reportingPathologist')[0]?.textContent || ''
    };
  });
}

// --- FHIR -> console record mapping ----------------------------------------

function mapPatient(patient) {
  const name = patient.name?.[0] || {};
  const address = patient.address?.[0] || {};
  return {
    identifier: {
      system: IHI_SYSTEM,
      value: patient.identifier?.find(i => i.system === IHI_SYSTEM)?.value || null
    },
    name: {
      given: (name.given || []).join(' '),
      family: Array.isArray(name.family) ? name.family.join(' ') : (name.family || '')
    },
    gender: patient.gender,
    birthDate: patient.birthDate,
    address: {
      line: address.line || [],
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country
    },
    telecom: patient.telecom?.[0]?.value || null
  };
}

function mapAllergy(resource) {
  const substance = resource.substance?.coding?.[0] || {};
  const manifestation = resource.reaction?.[0]?.manifestation?.[0]?.coding?.[0];
  return {
    substance: { code: substance.code, display: substance.display || resource.substance?.text, system: 'SNOMED CT-AU' },
    ...(manifestation ? { reaction: { code: manifestation.code, display: manifestation.display, system: 'SNOMED CT-AU' } } : {}),
    criticality: resource.criticality,
    type: resource.type,
    category: resource.category
  };
}

/** Split "80 mg tablet, once daily" back into the dose/frequency the panel shows. */
function splitDosage(text) {
  const [dose, ...rest] = String(text || '').split(',');
  return { dose: (dose || '').trim(), frequency: rest.join(',').trim() };
}

function mapMedicationOrder(order, medicationsById) {
  const medId = order.medicationReference?.reference?.split('/').pop();
  const medication = medicationsById[medId];
  const coding = medication?.code?.coding?.[0];
  const genericName = medication?.extension
    ?.find(e => e.url.endsWith('/medication-generic-name'))?.valueString;
  const { dose, frequency } = splitDosage(order.dosageInstruction?.[0]?.text);
  return {
    name: genericName || coding?.display || order.medicationReference?.display || 'Unknown medication',
    amtCode: coding?.code || null,
    dose,
    frequency,
    prescribed: order.dateWritten,
    prescriber: order.prescriber?.display || null,
    status: order.status
  };
}

function mapDispense(dispense, medicationsById) {
  const medId = dispense.medicationReference?.reference?.split('/').pop();
  const coding = medicationsById[medId]?.code?.coding?.[0];
  return {
    medication: coding?.display || dispense.medicationReference?.display || 'Unknown medication',
    quantity: dispense.quantity?.extension?.find(e => e.url.endsWith('/medicationdispense-quantity-description'))?.valueString
      || (dispense.quantity ? `${dispense.quantity.value} ${dispense.quantity.unit}` : null),
    dispensedOn: dispense.whenHandedOver,
    dispenser: dispense.dispenser?.display || null
  };
}

function mapDocument(doc) {
  const coding = doc.type?.coding?.[0] || {};
  return {
    id: doc.id,
    title: coding.display || doc.description,
    code: coding.code,
    description: doc.description,
    author: doc.author?.[0]?.display || null,
    custodian: doc.custodian?.display || null,
    created: doc.created,
    contentType: doc.content?.[0]?.attachment?.contentType || null
  };
}

function mapBenefit(eob) {
  const item = eob.item?.[0] || {};
  return {
    itemCode: item.service?.coding?.[0]?.code || null,
    description: item.service?.text || item.service?.coding?.[0]?.display || null,
    date: item.servicedDate || eob.created,
    benefit: item.net?.value ?? null,
    provider: eob.organization?.display || null
  };
}

// --- retrieval sequence -----------------------------------------------------

const STEPS = [
  { id: 'token', label: 'Authenticate', method: 'POST', path: '/api/oauth/token/provider' },
  { id: 'locate', label: 'Locate record', method: 'GET', path: '/Patient?identifier={ihi}&_elements=identifier' },
  { id: 'access', label: 'Gain access', method: 'POST', path: '/Patient/$access' },
  { id: 'demographics', label: 'Demographics', method: 'GET', path: '/Patient/{id}' },
  { id: 'allergies', label: 'Allergies & adverse reactions', method: 'GET', path: '/AllergyIntolerance?patient={id}' },
  { id: 'prescriptions', label: 'Prescriptions', method: 'GET', path: '/MedicationOrder?patient={id}' },
  { id: 'dispenses', label: 'Dispense records', method: 'GET', path: '/MedicationDispense?patient={id}' },
  { id: 'documents', label: 'Document list', method: 'GET', path: '/DocumentReference?patient={id}' },
  { id: 'shs', label: 'Shared Health Summary', method: 'GET', path: '/Binary/{docId}' },
  { id: 'pathology', label: 'Pathology view', method: 'GET', path: '/Binary/View?viewtype=PathologyView' },
  { id: 'pbs', label: 'PBS claims', method: 'GET', path: '/ExplanationOfBenefit?coverage.plan=PBS' }
];

export const RETRIEVAL_STEPS = STEPS;

/**
 * Run the full retrieval against the gateway.
 *
 * @param {object}   options
 * @param {string}   options.ihi          IHI of the record to retrieve.
 * @param {string}   options.accessType   GeneralAccess | AccessCode | EmergencyAccess.
 * @param {string}   [options.accessCode] Required when accessType is AccessCode.
 * @param {object}   [options.authHeaders] Matilda auth headers for /api/mhr/token.
 * @param {function} [options.onEvent]    Called with each interaction as it starts and finishes.
 * @param {function} [options.onRecord]   Called with a partial record each time new data lands.
 * @param {AbortSignal} [options.signal]
 */
export async function runMhrRetrieval({
  ihi = DEMO_IHI,
  accessType = 'GeneralAccess',
  accessCode,
  authHeaders = {},
  onEvent = () => {},
  onRecord = () => {},
  signal
} = {}) {
  const emit = (id, patch) => {
    const step = STEPS.find(s => s.id === id);
    onEvent({ ...step, ...patch, id });
  };

  const record = {
    source: { system: 'My Health Record', gateway: null, accessType, ihi, retrievedAt: null },
    document: null,
    patient: null,
    clinicalSections: { allergies: [], medications: [], medicalHistory: [], immunisations: [] },
    mhr: { documents: [], dispenses: [], pathology: [], pbs: [] }
  };
  const publish = () => onRecord(JSON.parse(JSON.stringify(record)));

  let gateway = null;
  let headers = null;

  /** Issue one gateway call, timing it and reporting it as a feed event. */
  const call = async (id, { method = 'GET', url, body, contentType, describe }) => {
    emit(id, { status: 'pending', detail: null, durationMs: null });
    const startedAt = performance.now();
    try {
      const res = await fetch(url, {
        method,
        signal,
        headers: {
          Accept: 'application/json+fhir',
          ...headers,
          ...(body ? { 'Content-Type': contentType || 'application/json+fhir' } : {})
        },
        ...(body ? { body: typeof body === 'string' ? body : JSON.stringify(body) } : {})
      });
      const durationMs = Math.round(performance.now() - startedAt);
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        throw new MhrError(outcomeText(payload) || `Gateway returned ${res.status}`, { status: res.status, outcome: payload });
      }
      emit(id, { status: 'ok', durationMs, detail: describe ? describe(payload) : null, url });
      return payload;
    } catch (error) {
      const durationMs = Math.round(performance.now() - startedAt);
      if (error.name === 'AbortError') {
        emit(id, { status: 'cancelled', durationMs });
        throw error;
      }
      emit(id, { status: 'error', durationMs, detail: error.message, url });
      throw error;
    }
  };

  // 1. Provider authentication — the assertion is signed server-side.
  emit('token', { status: 'pending' });
  const tokenStartedAt = performance.now();
  const tokenRes = await fetch('/api/mhr/token', { method: 'POST', headers: authHeaders, signal });
  const tokenBody = await tokenRes.json().catch(() => null);
  if (!tokenRes.ok || !tokenBody?.access_token) {
    const message = tokenBody?.error || `Token request failed (${tokenRes.status})`;
    emit('token', { status: 'error', durationMs: Math.round(performance.now() - tokenStartedAt), detail: message });
    throw new MhrError(message, { status: tokenRes.status });
  }
  gateway = tokenBody.gateway;
  headers = {
    Authorization: `Bearer ${tokenBody.access_token}`,
    'App-Id': gateway.appId,
    'App-Version': gateway.appVersion
  };
  record.source.gateway = `${gateway.mock ? 'Mock ' : ''}MHR FHIR Gateway ${gateway.apiVersion} · FHIR ${gateway.fhirVersion}`;
  emit('token', {
    status: 'ok',
    durationMs: Math.round(performance.now() - tokenStartedAt),
    detail: `HPI-O ${gateway.organisationID}`
  });
  publish();

  const base = gateway.base;

  // 2. Confirm the record exists without gaining access to it.
  const located = await call('locate', {
    url: `${base}/Patient?identifier=${encodeURIComponent(ihi)}&_elements=identifier`,
    describe: b => (b?.total ? `IHI ${ihi} found` : 'No record found')
  });
  if (!located?.total) {
    throw new MhrError(`No My Health Record exists for IHI ${ihi}.`);
  }

  // 3. Gain access. EmergencyAccess is audited and the individual is notified.
  const accessResult = await call('access', {
    method: 'POST',
    url: `${base}/Patient/$access`,
    body: {
      resourceType: 'Parameters',
      parameter: [
        { name: 'subject', valueIdentifier: { system: IHI_SYSTEM, value: ihi } },
        { name: 'accessType', valueString: accessType },
        ...(accessCode ? [{ name: 'accessCode', valueString: accessCode }] : [])
      ]
    },
    describe: b => b?.parameter?.find(p => p.name === 'accessType')?.valueString || 'Granted'
  });
  const patientResource = accessResult?.parameter?.find(p => p.name === 'patient')?.resource;
  const logicalId = patientResource?.id;
  if (!logicalId) throw new MhrError('The gateway did not return a patient logical identifier.');
  record.source.logicalId = logicalId;

  // 4. Demographics.
  const patient = await call('demographics', {
    url: `${base}/Patient/${logicalId}`,
    describe: p => [p?.name?.[0]?.given?.join(' '), p?.name?.[0]?.family].flat().filter(Boolean).join(' ')
  });
  record.patient = mapPatient(patient);
  publish();

  // 5. Allergies (from the most recent Shared Health Summary).
  const allergyBundle = await call('allergies', {
    url: `${base}/AllergyIntolerance?patient=${logicalId}&reporter._type=Practitioner`,
    describe: b => `${b?.total || 0} recorded`
  });
  record.clinicalSections.allergies = entries(allergyBundle, 'AllergyIntolerance').map(mapAllergy);
  publish();

  // 6. Prescriptions.
  const orderBundle = await call('prescriptions', {
    url: `${base}/MedicationOrder?patient=${logicalId}`,
    describe: b => `${entries(b, 'MedicationOrder').length} active`
  });
  const orderMeds = Object.fromEntries(entries(orderBundle, 'Medication').map(m => [m.id, m]));
  record.clinicalSections.medications = entries(orderBundle, 'MedicationOrder')
    .map(o => mapMedicationOrder(o, orderMeds));
  publish();

  // 7. Dispense records.
  const dispenseBundle = await call('dispenses', {
    url: `${base}/MedicationDispense?patient=${logicalId}&_include=MedicationDispense:authorizingPrescription`,
    describe: b => `${entries(b, 'MedicationDispense').length} dispensed`
  });
  const dispenseMeds = Object.fromEntries(entries(dispenseBundle, 'Medication').map(m => [m.id, m]));
  record.mhr.dispenses = entries(dispenseBundle, 'MedicationDispense')
    .map(d => mapDispense(d, dispenseMeds))
    .sort((a, b) => String(b.dispensedOn).localeCompare(String(a.dispensedOn)));
  publish();

  // 8. Document list. `class` and `type` repeat to cover several document kinds.
  const docClasses = [
    DOC_CLASS.sharedHealthSummary,
    DOC_CLASS.eventSummary,
    DOC_CLASS.dischargeSummary,
    DOC_CLASS.pathologyReport,
    DOC_CLASS.diagnosticImaging
  ].map(c => `class=${encodeURIComponent(c)}`).join('&');
  const documentBundle = await call('documents', {
    url: `${base}/DocumentReference?patient=${logicalId}&${docClasses}&status=current`,
    describe: b => `${b?.total || 0} documents`
  });
  const documents = entries(documentBundle, 'DocumentReference');
  record.mhr.documents = documents.map(mapDocument)
    .sort((a, b) => String(b.created).localeCompare(String(a.created)));
  publish();

  // 9. Retrieve the Shared Health Summary CDA — conditions and immunisations
  //    are only available inside the document payload.
  const shs = documents.find(d => d.type?.coding?.[0]?.code === '60591-5');
  if (shs) {
    const binary = await call('shs', {
      url: `${base}/Binary/${shs.id}?patient=${logicalId}`,
      describe: () => shs.description || 'Retrieved'
    });
    const parsed = parseSharedHealthSummary(decodeBase64(binary.content));
    record.clinicalSections.medicalHistory = parsed.medicalHistory;
    record.clinicalSections.immunisations = parsed.immunisations;
    record.document = {
      id: shs.masterIdentifier?.value,
      title: parsed.title,
      effectiveTime: shs.created,
      custodian: parsed.custodian,
      language: 'en-AU'
    };
    publish();
  } else {
    emit('shs', { status: 'skipped', detail: 'No Shared Health Summary in this record' });
  }

  // 10. Pathology view (v5.0: returned after a 0-day delay, Appendix A.9).
  try {
    const view = await call('pathology', {
      url: `${base}/Binary/View?patient=${logicalId}&viewtype=PathologyView&version=2.0`,
      describe: () => 'PathologyView 2.0'
    });
    record.mhr.pathology = parsePathologyView(decodeBase64(view.content));
    publish();
  } catch (error) {
    if (error.name === 'AbortError') throw error;
    // A missing view is not fatal — the rest of the record still stands.
  }

  // 11. PBS claims.
  try {
    const pbsBundle = await call('pbs', {
      url: `${base}/ExplanationOfBenefit?patientreference=${logicalId}&coverage.plan=PBS`,
      describe: b => `${b?.total || 0} claims`
    });
    record.mhr.pbs = entries(pbsBundle, 'ExplanationOfBenefit').map(mapBenefit)
      .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  } catch (error) {
    if (error.name === 'AbortError') throw error;
  }

  record.source.retrievedAt = new Date().toISOString();
  publish();
  return record;
}

export default runMhrRetrieval;
